import type { CalendarDate, Instant, Priority, TaskStatus } from "@hq/contracts";
import { addDays, instantAt, type NowContext, todayOf, weekdayOf } from "./time";

export type QuickAddTokenKind =
  | "date"
  | "time"
  | "duration"
  | "priority"
  | "project"
  | "label"
  | "flag";

export type QuickAddToken = { kind: QuickAddTokenKind; start: number; end: number; text: string };

export type QuickAddResult = {
  title: string;
  dueDate?: CalendarDate;
  plannedDate?: CalendarDate;
  scheduledAt?: Instant;
  scheduledDurationMinutes?: number;
  priority?: Priority;
  /** Unresolved project name after `#`. The caller matches it against projects. */
  projectName?: string;
  /** `@tag` tokens, normalized. Stored as the task's `tags`. */
  labels: string[];
  evening?: boolean;
  status?: TaskStatus;
  /** Recognised spans in the original input, for live chips. */
  tokens: QuickAddToken[];
};

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const WEEKDAY_ABBR: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  tues: 2,
  wed: 3,
  thu: 4,
  thur: 4,
  thurs: 4,
  fri: 5,
  sat: 6,
};
const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

function monthIndex(s: string): number {
  const l = s.toLowerCase();
  const i = MONTHS.findIndex(
    (m) => m === l || m.slice(0, 3) === l || (l.length >= 3 && m.startsWith(l)),
  );
  return i;
}

type Word = { text: string; lower: string; start: number; end: number };

function words(input: string): Word[] {
  const out: Word[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input))) {
    out.push({ text: m[0], lower: m[0].toLowerCase(), start: m.index, end: m.index + m[0].length });
  }
  return out;
}

function nextWeekday(today: CalendarDate, weekday: number, forceNextWeek: boolean): CalendarDate {
  const cur = weekdayOf(today); // 0 = Sunday
  if (!forceNextWeek) return addDays(today, (weekday - cur + 7) % 7);
  // "next <weekday>" is that day in the following ISO week (Monday to Sunday).
  const isoCur = cur === 0 ? 7 : cur;
  const nextMonday = addDays(today, 8 - isoCur);
  return addDays(nextMonday, (weekday + 6) % 7);
}

const TIME_RE = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/i;
function parseTime(w: string): { hour: number; minute: number } | undefined {
  const l = w.toLowerCase();
  if (l === "noon") return { hour: 12, minute: 0 };
  if (l === "midnight") return { hour: 0, minute: 0 };
  const m = TIME_RE.exec(l);
  if (!m) return undefined;
  let hour = Number(m[1]);
  const minute = m[2] ? Number(m[2]) : 0;
  const ampm = m[3];
  if (!ampm && !m[2]) return undefined; // a bare number is not a time
  if (hour > 23 || minute > 59) return undefined;
  if (ampm === "pm" && hour < 12) hour += 12;
  if (ampm === "am" && hour === 12) hour = 0;
  return { hour, minute };
}

const DURATION_RE = /^(?:(\d+)h)?(?:(\d+)m)?$/i;
function parseDuration(w: string): number | undefined {
  const l = w.toLowerCase();
  if (l === "1h" || l === "1hr" || l === "1hour") return 60;
  const m = DURATION_RE.exec(l);
  if (!m || (!m[1] && !m[2])) return undefined;
  return (m[1] ? Number(m[1]) * 60 : 0) + (m[2] ? Number(m[2]) : 0);
}

/**
 * Parse natural-language capture text such as
 * `Pay rent tomorrow 9am p1 #Personal @errand for 30m`.
 * Date-only plans a work day; a time makes it scheduled (`scheduledAt`).
 * `by`/`due` before a date always means deadline.
 */
export function parseQuickAdd(input: string, ctx: NowContext): QuickAddResult {
  const today = todayOf(ctx);
  const ws = words(input);
  const consumed = new Set<number>();
  const tokens: QuickAddToken[] = [];
  const result: QuickAddResult = { title: "", labels: [], tokens };
  let date: CalendarDate | undefined;

  let time: { hour: number; minute: number } | undefined;

  const mark = (kind: QuickAddTokenKind, from: number, to: number) => {
    for (let i = from; i <= to; i++) consumed.add(i);
    const a = ws[from]!;
    const b = ws[to]!;
    tokens.push({ kind, start: a.start, end: b.end, text: input.slice(a.start, b.end) });
  };

  const tryDate = (i: number): { date: CalendarDate; length: number } | undefined => {
    const w = ws[i]!;
    const l = w.lower;
    const n1 = ws[i + 1]?.lower;
    const n2 = ws[i + 2]?.lower;
    if (l === "today" || l === "tod") return { date: today, length: 1 };
    if (l === "tomorrow" || l === "tmr" || l === "tmrw")
      return { date: addDays(today, 1), length: 1 };
    if (l === "yesterday") return { date: addDays(today, -1), length: 1 };
    if (l === "tonight") return { date: today, length: 1 };
    if (l === "next" && n1) {
      if (n1 === "week") return { date: nextWeekday(today, 1, true), length: 2 };
      if (n1 === "month") {
        const [y, m] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
        return {
          date: `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, "0")}-01`,
          length: 2,
        };
      }
      const wd = WEEKDAYS.indexOf(n1) >= 0 ? WEEKDAYS.indexOf(n1) : WEEKDAY_ABBR[n1];
      if (wd !== undefined) return { date: nextWeekday(today, wd, true), length: 2 };
    }
    if (l === "in" && n1 && n2) {
      const n = Number(n1);
      if (Number.isInteger(n) && n > 0) {
        if (n2.startsWith("day")) return { date: addDays(today, n), length: 3 };
        if (n2.startsWith("week")) return { date: addDays(today, n * 7), length: 3 };
      }
    }
    {
      const wd = WEEKDAYS.indexOf(l) >= 0 ? WEEKDAYS.indexOf(l) : WEEKDAY_ABBR[l];
      if (wd !== undefined) return { date: nextWeekday(today, wd, false), length: 1 };
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(l)) return { date: l, length: 1 };
    {
      const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(l);
      if (m) {
        const mo = Number(m[1]);
        const d = Number(m[2]);
        let y = m[3] ? Number(m[3]) : Number(today.slice(0, 4));
        if (y < 100) y += 2000;
        if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
          const cand = `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          return { date: !m[3] && cand < today ? `${y + 1}${cand.slice(4)}` : cand, length: 1 };
        }
      }
    }
    // "sep 20" / "20 sep" / "sep 20 2027"
    const mi = monthIndex(l);
    if (mi >= 0 && n1 && /^\d{1,2}(st|nd|rd|th)?$/.test(n1)) {
      const d = Number.parseInt(n1, 10);
      const hasYear = n2 !== undefined && /^\d{4}$/.test(n2);
      const y = hasYear ? Number(n2) : Number(today.slice(0, 4));
      const cand = `${y}-${String(mi + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      return {
        date: !hasYear && cand < today ? `${y + 1}${cand.slice(4)}` : cand,
        length: hasYear ? 3 : 2,
      };
    }
    if (/^\d{1,2}(st|nd|rd|th)?$/.test(l) && n1 && monthIndex(n1) >= 0) {
      const d = Number.parseInt(l, 10);
      const mo = monthIndex(n1) + 1;
      const hasYear = n2 !== undefined && /^\d{4}$/.test(n2);
      const y = hasYear ? Number(n2) : Number(today.slice(0, 4));
      const cand = `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      return {
        date: !hasYear && cand < today ? `${y + 1}${cand.slice(4)}` : cand,
        length: hasYear ? 3 : 2,
      };
    }
    return undefined;
  };

  for (let i = 0; i < ws.length; i++) {
    if (consumed.has(i)) continue;
    const w = ws[i]!;
    const l = w.lower;

    if (w.text.length > 1 && w.text.startsWith("#")) {
      result.projectName = w.text.slice(1);
      mark("project", i, i);
      continue;
    }
    if (w.text.length > 1 && w.text.startsWith("@")) {
      result.labels.push(w.text.slice(1));
      mark("label", i, i);
      continue;
    }
    if (/^p[1-3]$/.test(l)) {
      result.priority = l === "p1" ? "high" : l === "p2" ? "medium" : "low";
      mark("priority", i, i);
      continue;
    }
    if (/^!(high|medium|med|low)$/.test(l)) {
      result.priority = l === "!high" ? "high" : l === "!low" ? "low" : "medium";
      mark("priority", i, i);
      continue;
    }
    if (l === "someday") {
      result.status = "someday";
      mark("flag", i, i);
      continue;
    }
    if (l === "evening" || l === "tonight") {
      result.evening = true;
      if (l === "tonight") {
        date ??= today;
      }
      mark("flag", i, i);
      continue;
    }
    if ((l === "by" || l === "due" || l === "deadline" || l === "on") && i + 1 < ws.length) {
      const d = tryDate(i + 1);
      if (d) {
        if (l === "on") date = d.date;
        else result.dueDate = d.date;
        mark("date", i, i + d.length);
        continue;
      }
    }
    if (l === "at" && i + 1 < ws.length) {
      const t = parseTime(ws[i + 1]!.text);
      if (t) {
        time = t;
        mark("time", i, i + 1);
        continue;
      }
    }
    if (l === "for" && i + 1 < ws.length) {
      const d = parseDuration(ws[i + 1]!.text);
      if (d) {
        result.scheduledDurationMinutes = d;
        mark("duration", i, i + 1);
        continue;
      }
    }
    const d = tryDate(i);
    if (d) {
      date = d.date;
      mark("date", i, i + d.length - 1);
      continue;
    }
    const t = parseTime(w.text);
    if (
      t &&
      (l.includes("am") || l.includes("pm") || l.includes(":") || l === "noon" || l === "midnight")
    ) {
      time = t;
      mark("time", i, i);
      continue;
    }
  }

  if (time) {
    result.scheduledAt = instantAt(
      date ?? result.dueDate ?? today,
      time.hour,
      time.minute,
      ctx.timeZone,
    );
  } else if (date) {
    result.plannedDate = date;
  }
  if (result.scheduledDurationMinutes !== undefined && !result.scheduledAt) {
    // A duration without a time is an estimate, not a schedule.
    delete result.scheduledDurationMinutes;
  }

  result.title = ws
    .filter((_, i) => !consumed.has(i))
    .map((w) => w.text)
    .join(" ")
    .trim();
  tokens.sort((a, b) => a.start - b.start);
  return result;
}
